import { useState } from "react";
import { useDispatch } from "react-redux";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import ConfirmationModal from "./ConfirmationModal";
import ConferenceModal from "./ConferenceModal";
import { useDialerServiceUrl, useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import { useConferenceCallLeadUuids, useConferenceCallSource } from "../../reducers/dialerReducer/selectors";
import { setConferenceCallParams } from "../../reducers/dialerReducer/dialerReducer";
import ConferenceFlyout from "./ConferenceFlyout";

const ACTIVE_DIALER_SESSION_TOKEN_KEY = "conference-dialer-session-token";

const ConferenceCall = () => {
  const dispatch = useDispatch();
  const dialerServiceUrl = useDialerServiceUrl();
  const featureFlags = useFeatureFlags();

  const selectedLeadUuids = useConferenceCallLeadUuids() || [];
  const callSource = useConferenceCallSource() || "Lead Index Brivity Dialer";

  const [callModalOpen, setCallModalOpen] = useState(false);
  const [startCall, setStartCall] = useState(false);
  const [conferenceUuid, setConferenceUuid] = useState(null);
  const [dialerServiceToken, setDialerServiceToken] = useState(() =>
    window.sessionStorage.getItem(ACTIVE_DIALER_SESSION_TOKEN_KEY),
  );
  const [isRecoveredSession, setIsRecoveredSession] = useState(() =>
    Boolean(window.sessionStorage.getItem(ACTIVE_DIALER_SESSION_TOKEN_KEY)),
  );
  const [isLoading, setIsLoading] = useState(false);

  const callConfirmationOpen = selectedLeadUuids.length > 0 && !startCall && !callModalOpen;

  const createDialerSession = () => {
    if (featureFlags.dialerImprovementUpdates) {
      window.clarity?.("event", "conferenceDialerStart");
    }
    setIsLoading(true);
    caseTransformingAxios
      .post("/dialer/create_dialer_session", {
        callSource,
        disableAutodial: featureFlags.dialerImprovementUpdates,
        leadUuids: selectedLeadUuids,
      })
      .then((resp) => {
        setConferenceUuid(resp.data.conferenceUuid);
        window.sessionStorage.setItem(ACTIVE_DIALER_SESSION_TOKEN_KEY, resp.data.token);
        setDialerServiceToken(resp.data.token);
        setIsRecoveredSession(false);
        setIsLoading(false);
        setStartCall(true);

        if (!featureFlags.dialerImprovementUpdates) {
          setCallModalOpen(true);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const closeCallModal = () => {
    dispatch(setConferenceCallParams());
    setCallModalOpen(false);
    setStartCall(false);
    setConferenceUuid(null);
    setDialerServiceToken(null);
    window.sessionStorage.removeItem(ACTIVE_DIALER_SESSION_TOKEN_KEY);
    setIsRecoveredSession(false);
  };

  if (selectedLeadUuids.length === 0 && !isRecoveredSession) {
    return null;
  }

  return (
    <>
      {!featureFlags.dialerImprovementUpdates && (
        <>
          <ConfirmationModal
            show={callConfirmationOpen}
            isLoading={isLoading}
            selectedLeadUuids={selectedLeadUuids}
            createDialerSession={createDialerSession}
            closeConfirmationModal={closeCallModal}
          />
          <ConferenceModal
            dialerServiceUrl={dialerServiceUrl}
            conferenceUuid={conferenceUuid}
            dialerServiceToken={dialerServiceToken}
            selectedLeadUuids={selectedLeadUuids}
            closeCallModal={closeCallModal}
            show={callModalOpen}
            startCall={startCall}
          />
        </>
      )}
      {featureFlags.dialerImprovementUpdates && (
        <ConferenceFlyout
          show={selectedLeadUuids.length > 0 || isRecoveredSession}
          onClose={closeCallModal}
          selectedLeadUuids={selectedLeadUuids}
          createDialerSession={createDialerSession}
          dialerServiceUrl={dialerServiceUrl}
          dialerServiceToken={dialerServiceToken}
          isRecoveredSession={isRecoveredSession}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ConferenceCall;
